import React, { createContext, useContext, useReducer, useEffect } from 'react';
import localStorageService from '../services/storage/localStorageService';
import apiService from '../services/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Detectar si estamos en modo web (navegador) o Electron
const isWebMode = !(window.electron && window.electron.ipcRenderer);

// Estado inicial
const initialState = {
  // Usuario y autenticación
  user: null,
  isAuthenticated: false,

  // Modo de operación
  isWebMode: isWebMode,

  // Almacén activo
  activeWarehouse: null,

  // Configuración de la app
  config: {
    theme: 'light',
    language: 'es',
    currency: 'MXN',
    taxRate: 0.16,
    defaultPrinter: null
  },

  // Estado de la aplicación
  currentView: 'sales',
  loading: false,
  error: null,

  // Carrito de compras
  cart: [],
  cartTotal: 0,

  // Productos y categorías
  products: [],
  categories: [],

  // Configuración de impresoras
  printers: [],
  selectedPrinter: null,

  // UI - Sidebar
  sidebarCollapsed: false
};

// Tipos de acciones
const ActionTypes = {
  // Usuario
  SET_USER: 'SET_USER',
  LOGOUT: 'LOGOUT',

  // Almacén
  SET_ACTIVE_WAREHOUSE: 'SET_ACTIVE_WAREHOUSE',

  // Configuración
  UPDATE_CONFIG: 'UPDATE_CONFIG',

  // UI
  SET_VIEW: 'SET_VIEW',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',

  // Carrito
  ADD_TO_CART: 'ADD_TO_CART',
  REMOVE_FROM_CART: 'REMOVE_FROM_CART',
  UPDATE_CART_ITEM: 'UPDATE_CART_ITEM',
  CLEAR_CART: 'CLEAR_CART',
  SET_CART: 'SET_CART',

  // Productos
  SET_PRODUCTS: 'SET_PRODUCTS',
  ADD_PRODUCT: 'ADD_PRODUCT',
  UPDATE_PRODUCT: 'UPDATE_PRODUCT',
  DELETE_PRODUCT: 'DELETE_PRODUCT',

  // Impresoras
  SET_PRINTERS: 'SET_PRINTERS',
  SET_SELECTED_PRINTER: 'SET_SELECTED_PRINTER',

  // Sidebar
  SET_SIDEBAR_COLLAPSED: 'SET_SIDEBAR_COLLAPSED',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload
      };

    case ActionTypes.LOGOUT:
      console.log('🔓 Reducer LOGOUT ejecutado');
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        activeWarehouse: null,
        cart: [],
        cartTotal: 0
      };

    case ActionTypes.SET_ACTIVE_WAREHOUSE:
      // Guardar almacén activo en localStorage
      if (action.payload) {
        localStorage.setItem('active_warehouse', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('active_warehouse');
      }
      return {
        ...state,
        activeWarehouse: action.payload
      };

    case ActionTypes.UPDATE_CONFIG:
      const newConfig = { ...state.config, ...action.payload };
      localStorageService.setAppConfig(newConfig);
      return {
        ...state,
        config: newConfig
      };

    case ActionTypes.SET_VIEW:
      return {
        ...state,
        currentView: action.payload
      };

    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case ActionTypes.ADD_TO_CART:
      const existingItem = state.cart.find(item => item.id === action.payload.id);
      let newCart;

      if (existingItem) {
        newCart = state.cart.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: item.quantity + (action.payload.quantity || 1) }
            : item
        );
      } else {
        newCart = [...state.cart, { ...action.payload, quantity: action.payload.quantity || 1 }];
      }

      const newTotal = newCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      localStorageService.setCart(newCart);

      return {
        ...state,
        cart: newCart,
        cartTotal: newTotal
      };

    case ActionTypes.REMOVE_FROM_CART:
      const filteredCart = state.cart.filter(item => item.id !== action.payload);
      const filteredTotal = filteredCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      localStorageService.setCart(filteredCart);

      return {
        ...state,
        cart: filteredCart,
        cartTotal: filteredTotal
      };

    case ActionTypes.UPDATE_CART_ITEM:
      const updatedCart = state.cart.map(item =>
        item.id === action.payload.id
          ? { ...item, ...action.payload.updates }
          : item
      );
      const updatedTotal = updatedCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      localStorageService.setCart(updatedCart);

      return {
        ...state,
        cart: updatedCart,
        cartTotal: updatedTotal
      };

    case ActionTypes.CLEAR_CART:
      localStorageService.clearCart();
      return {
        ...state,
        cart: [],
        cartTotal: 0
      };

    case ActionTypes.SET_CART:
      const cartTotal = action.payload.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      return {
        ...state,
        cart: action.payload,
        cartTotal
      };

    case ActionTypes.SET_PRODUCTS:
      return {
        ...state,
        products: action.payload
      };

    case ActionTypes.ADD_PRODUCT:
      return {
        ...state,
        products: [...state.products, action.payload]
      };

    case ActionTypes.UPDATE_PRODUCT:
      return {
        ...state,
        products: state.products.map(product =>
          product.id === action.payload.id
            ? { ...product, ...action.payload.updates }
            : product
        )
      };

    case ActionTypes.DELETE_PRODUCT:
      return {
        ...state,
        products: state.products.filter(product => product.id !== action.payload)
      };

    case ActionTypes.SET_PRINTERS:
      return {
        ...state,
        printers: action.payload
      };

    case ActionTypes.SET_SELECTED_PRINTER:
      return {
        ...state,
        selectedPrinter: action.payload
      };

    case ActionTypes.SET_SIDEBAR_COLLAPSED:
      return {
        ...state,
        sidebarCollapsed: action.payload
      };

    case ActionTypes.TOGGLE_SIDEBAR:
      return {
        ...state,
        sidebarCollapsed: !state.sidebarCollapsed
      };

    default:
      return state;
  }
};

// Crear contexto
const AppContext = createContext();

// Hook personalizado para usar el contexto
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp debe ser usado dentro de AppProvider');
  }
  return context;
};

// Provider del contexto
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Cargar datos iniciales
  useEffect(() => {
    // Cargar configuración guardada (NO relacionada con autenticación)
    const savedConfig = localStorageService.getAppConfig();
    dispatch({ type: ActionTypes.UPDATE_CONFIG, payload: savedConfig });

    // SIEMPRE limpiar datos de autenticación al iniciar para forzar login
    // Esto mantiene otras configuraciones como api_url, pos_nombre_pc, etc.
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('active_warehouse');
    apiService.token = null;

    // Limpiar carrito al iniciar (opcional, para empezar limpio)
    localStorageService.clearCart();

    console.log('🔐 Sesión limpiada al iniciar - Se requiere login');
  }, []);

  // Acciones
  const actions = {
    // Usuario
    setUser: (user) => {
      localStorageService.setCurrentUser(user);
      dispatch({ type: ActionTypes.SET_USER, payload: user });
    },

    login: async (usuario, password) => {
      try {
        dispatch({ type: ActionTypes.SET_LOADING, payload: true });
        dispatch({ type: ActionTypes.CLEAR_ERROR });

        const result = await apiService.login(usuario, password);

        if (result.success) {
          const userData = result.data;
          localStorageService.setCurrentUser(userData);

          // Guardar permisos del usuario
          const userPermissions = userData.permisos || [];
          localStorage.setItem('userPermissions', JSON.stringify(userPermissions));

          dispatch({ type: ActionTypes.SET_USER, payload: userData });

          // Determinar la primera vista permitida
          const viewPermissionsMap = isWebMode ? [
            { view: 'reports', permission: 'reportes_ventas_ver' },
            { view: 'reporte-ventas-mostrador', permission: 'reporte_ventas_mostrador' },
            { view: 'facturacion', permission: 'facturacion_ver' },
            { view: 'cuentas-por-cobrar', permission: 'cxc_ver' },
            { view: 'products', permission: 'productos_ver' },
            { view: 'customers', permission: 'clientes_ver' },
            { view: 'settings', permission: 'configuracion_ver' }
          ] : [
            { view: 'ventas-mostrador', permission: 'ventas_mostrador_ver' },
            { view: 'sales', permission: 'ventas_ver' },
            { view: 'pedidos', permission: 'pedidos_ver' },
            { view: 'corte-caja', permission: 'corte_caja_ver' },
            { view: 'cuentas-por-cobrar', permission: 'cxc_ver' },
            { view: 'products', permission: 'productos_ver' },
            { view: 'cambio-precios', permission: 'productos_editar' },
            { view: 'migracion-productos', permission: 'productos_ver' },
            { view: 'customers', permission: 'clientes_ver' },
            { view: 'almacenes', permission: 'almacenes_ver' },
            { view: 'proveedores', permission: 'proveedores_ver' },
            { view: 'ordenes-compra', permission: 'ordenes_compra_ver' },
            { view: 'cuentas-por-pagar', permission: 'cxp_ver' },
            { view: 'picking', permission: 'picking_ver' },
            { view: 'preparacion', permission: 'preparacion_ver' },
            { view: 'medido-hoy', permission: 'medido_hoy_ver' },
            { view: 'caratula-pedidos', permission: 'caratula_pedidos_ver' },
            { view: 'flete', permission: 'flete_ver' },
            { view: 'facturacion', permission: 'facturacion_ver' },
            { view: 'users', permission: 'usuarios_ver' },
            { view: 'roles', permission: 'roles_ver' },
            { view: 'reports', permission: 'reportes_ventas_ver' },
            { view: 'settings', permission: 'configuracion_ver' }
          ];

          // Encontrar la primera vista permitida
          const firstPermittedView = viewPermissionsMap.find(item =>
            userPermissions.includes(item.permission)
          );

          if (firstPermittedView) {
            dispatch({ type: ActionTypes.SET_VIEW, payload: firstPermittedView.view });
          } else {
            // Si no tiene ningún permiso específico, intentar con 'sales' por defecto
            dispatch({ type: ActionTypes.SET_VIEW, payload: 'sales' });
          }

          return { success: true, data: userData };
        } else {
          dispatch({ type: ActionTypes.SET_ERROR, payload: result.message });
          return { success: false, message: result.message };
        }
      } catch (error) {
        const errorMessage = error.message || 'Error de conexión con el servidor';
        dispatch({ type: ActionTypes.SET_ERROR, payload: errorMessage });
        return { success: false, message: errorMessage };
      } finally {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    },

    logout: async () => {
      try {
        await apiService.logout();
      } catch (error) {
        console.error('Error en logout:', error);
      } finally {
        // Limpiar estado local
        localStorageService.remove('current_user');
        localStorageService.remove('auth_token');
        localStorage.removeItem('userPermissions');
        localStorage.removeItem('active_warehouse');
        localStorageService.clearCart();
        dispatch({ type: ActionTypes.LOGOUT });
      }
    },

    // Almacén
    setActiveWarehouse: (warehouse) => {
      dispatch({ type: ActionTypes.SET_ACTIVE_WAREHOUSE, payload: warehouse });
    },

    // Configuración
    updateConfig: (config) => {
      dispatch({ type: ActionTypes.UPDATE_CONFIG, payload: config });
    },

    // UI
    setView: (view) => {
      dispatch({ type: ActionTypes.SET_VIEW, payload: view });
    },

    setLoading: (loading) => {
      dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
    },

    setError: (error) => {
      dispatch({ type: ActionTypes.SET_ERROR, payload: error });
    },

    clearError: () => {
      dispatch({ type: ActionTypes.CLEAR_ERROR });
    },

    showToast: (message, type = 'success') => {
      const toastOptions = {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      };

      switch (type) {
        case 'success':
          toast.success(message, toastOptions);
          break;
        case 'error':
          toast.error(message, toastOptions);
          break;
        case 'warning':
          toast.warning(message, toastOptions);
          break;
        case 'info':
          toast.info(message, toastOptions);
          break;
        default:
          toast(message, toastOptions);
      }
    },

    // Carrito
    addToCart: (product, quantity = 1) => {
      dispatch({ type: ActionTypes.ADD_TO_CART, payload: { ...product, quantity } });
    },

    removeFromCart: (productId) => {
      dispatch({ type: ActionTypes.REMOVE_FROM_CART, payload: productId });
    },

    updateCartItem: (productId, updates) => {
      dispatch({ type: ActionTypes.UPDATE_CART_ITEM, payload: { id: productId, updates } });
    },

    clearCart: () => {
      dispatch({ type: ActionTypes.CLEAR_CART });
    },

    // Productos
    setProducts: (products) => {
      dispatch({ type: ActionTypes.SET_PRODUCTS, payload: products });
    },

    loadProducts: async () => {
      try {
        dispatch({ type: ActionTypes.SET_LOADING, payload: true });
        const result = await apiService.getProductos();

        if (result.success) {
          dispatch({ type: ActionTypes.SET_PRODUCTS, payload: result.data });
          return { success: true, data: result.data };
        } else {
          dispatch({ type: ActionTypes.SET_ERROR, payload: result.message });
          return { success: false, message: result.message };
        }
      } catch (error) {
        const errorMessage = 'Error al cargar productos';
        dispatch({ type: ActionTypes.SET_ERROR, payload: errorMessage });
        return { success: false, message: errorMessage };
      } finally {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    },

    searchProducts: async (query) => {
      try {
        const result = await apiService.buscarProductos(query);
        if (result.success) {
          return { success: true, data: result.data };
        } else {
          return { success: false, message: result.message };
        }
      } catch (error) {
        return { success: false, message: 'Error al buscar productos' };
      }
    },

    addProduct: async (productData) => {
      try {
        const result = await apiService.crearProducto(productData);
        if (result.success) {
          dispatch({ type: ActionTypes.ADD_PRODUCT, payload: result.data });
          return { success: true, data: result.data };
        } else {
          return { success: false, message: result.message };
        }
      } catch (error) {
        return { success: false, message: 'Error al crear producto' };
      }
    },

    updateProduct: async (productId, updates) => {
      try {
        const result = await apiService.actualizarProducto({ id: productId, ...updates });
        if (result.success) {
          dispatch({ type: ActionTypes.UPDATE_PRODUCT, payload: { id: productId, updates } });
          return { success: true };
        } else {
          return { success: false, message: result.message };
        }
      } catch (error) {
        return { success: false, message: 'Error al actualizar producto' };
      }
    },

    deleteProduct: (productId) => {
      dispatch({ type: ActionTypes.DELETE_PRODUCT, payload: productId });
    },

    // Ventas
    createSale: async (saleData) => {
      try {
        dispatch({ type: ActionTypes.SET_LOADING, payload: true });
        const result = await apiService.crearVenta(saleData);

        if (result.success) {
          // Limpiar carrito después de venta exitosa
          dispatch({ type: ActionTypes.CLEAR_CART });
          return { success: true, data: result.data };
        } else {
          dispatch({ type: ActionTypes.SET_ERROR, payload: result.message });
          return { success: false, message: result.message };
        }
      } catch (error) {
        const errorMessage = 'Error al procesar la venta';
        dispatch({ type: ActionTypes.SET_ERROR, payload: errorMessage });
        return { success: false, message: errorMessage };
      } finally {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    },

    // Clientes
    loadClients: async () => {
      try {
        const result = await apiService.getClientes();
        if (result.success) {
          return { success: true, data: result.data };
        } else {
          return { success: false, message: result.message };
        }
      } catch (error) {
        return { success: false, message: 'Error al cargar clientes' };
      }
    },

    searchClients: async (query) => {
      try {
        const result = await apiService.buscarClientes(query);
        if (result.success) {
          return { success: true, data: result.data };
        } else {
          return { success: false, message: result.message };
        }
      } catch (error) {
        return { success: false, message: 'Error al buscar clientes' };
      }
    },

    // Impresoras
    setPrinters: (printers) => {
      dispatch({ type: ActionTypes.SET_PRINTERS, payload: printers });
    },

    setSelectedPrinter: (printer) => {
      dispatch({ type: ActionTypes.SET_SELECTED_PRINTER, payload: printer });
    },

    // Sidebar
    setSidebarCollapsed: (collapsed) => {
      dispatch({ type: ActionTypes.SET_SIDEBAR_COLLAPSED, payload: collapsed });
    },

    toggleSidebar: () => {
      dispatch({ type: ActionTypes.TOGGLE_SIDEBAR });
    }
  };

  const value = {
    ...state,
    ...actions
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </AppContext.Provider>
  );
};

export default AppContext;